/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {FormFieldAttribute} from '../../../../../../models/FormCategory';
import {EChartsOption} from 'echarts';
import {GeneralDataHandlingService} from '../../../../../../services/general.data-handling.service';
import {registerTheme} from '@siemens/ix-echarts';
import * as echarts from 'echarts/core';
import {NgxEchartsDirective} from 'ngx-echarts';
import {AppSettings} from '../../../../../../models/Constants';
import {PassportDto} from '../../../../../../models/Passport';
import {TranslateService} from '@ngx-translate/core';
import {ThemeService} from '../../../../../../services/theme.service';
import {PassportTranslationService} from '../../../../../../services/passport-translation.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-bar',
  imports: [NgxEchartsDirective],
  templateUrl: './bar.component.html',
  styleUrl: './bar.component.scss',
})
export class BarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dataHandler = inject(GeneralDataHandlingService);
  private readonly translate = inject(TranslateService);
  private readonly themeService = inject(ThemeService);
  private readonly passportTranslation = inject(PassportTranslationService);

  @Input({required: true}) attribute!: FormFieldAttribute;
  @Input({required: true}) passport!: PassportDto['data'];
  @Input({required: true}) schemaId!: string;

  options: EChartsOption = {};

  private changeLangSubscription?: Subscription;

  ngAfterViewInit() {
    const container = document.querySelector(
      `#line-chart-container-${this.attribute.name}`,
    ) as HTMLElement;
    if (container) {
      container.style.height = '400px';
    }
    setTimeout(() => {
      this.options = this.buildOptions();
    }, AppSettings.INPUT_TO_REQUEST_WAIT_TIME_IN_MS);
  }

  ngOnInit() {
    registerTheme(echarts);

    // Re-build the chart in the newly selected language once ngx-translate
    // has finished loading it. The language selection/loading itself is
    // already handled elsewhere; here we only react to the resulting event.
    this.changeLangSubscription = this.translate.onLangChange.subscribe(() => {
      this.options = this.buildOptions();
    });
  }

  ngOnDestroy(): void {
    this.changeLangSubscription?.unsubscribe();
  }

  getChartTheme() {
    return this.themeService.actualChartTheme;
  }

  buildOptions(): EChartsOption {
    const selectAttribute = this.attribute.attributes!.find(
      (e) => e.type === 'select',
    ) as any;
    const dataAttributes = this.attribute.attributes!.filter(
      (e) => e.type === 'number',
    );
    // Translate dimensions
    const translatedDimensions = [
      this.passportTranslation.translateByPath(
        selectAttribute.valuePath,
        this.schemaId,
        selectAttribute.translation.label,
        this.attribute.valuePath,
      ),
      ...dataAttributes.map((e) =>
        this.passportTranslation.translateByPath(
          e.valuePath,
          this.schemaId,
          e.translation.label,
          this.attribute.valuePath,
        ),
      ),
    ];
    const data = this.dataHandler.getValueByPath(
      this.passport,
      this.attribute.valuePath,
    );
    const sources = selectAttribute!.options.map((option: any) => [
      this.translateSelectValue(selectAttribute.valuePath, option.value),
      ...dataAttributes.map((attr) =>
        data
          .filter(
            (item: any) =>
              this.dataHandler.getValueByPath(
                item,
                selectAttribute!.valuePath,
              ) === option.value,
          )
          .reduce(
            (acc: any, item: any) =>
              (this.dataHandler.getValueByPath(item, attr.valuePath) || 0) +
              acc,
            0,
          ),
      ),
    ]);
    const axisDefinitions = {
      x: this.passportTranslation.translateByPath(
        selectAttribute.valuePath,
        this.schemaId,
        selectAttribute.translation.label,
        this.attribute.valuePath,
      ),
      unit: this.getUnit(dataAttributes[0].unit || ''),
    };
    return {
      grid: {
        left: 40,
        right: 0,
        top: 20,
        bottom: 90,
      },
      legend: {
        bottom: 'bottom',
      },
      dataset: {
        source: [translatedDimensions, ...sources],
      },
      xAxis: {
        name: axisDefinitions.x,
        type: 'category',
        nameLocation: 'middle',
        nameGap: 24,
      },
      yAxis: {
        axisLabel: {
          formatter: (value: number) => `${value} ${axisDefinitions.unit}`,
        },
      },
      series: Array.from({length: dataAttributes.length}, (_, _index) => ({
        type: 'bar',
      })),
    };
  }

  private translateSelectValue(valuePath: string, rawValue: string): string {
    const direct = this.passportTranslation.translateValueByPath(
      valuePath,
      this.schemaId,
      rawValue,
      rawValue,
    );
    if (direct !== rawValue) {
      return direct;
    }

    return this.passportTranslation.translateValueByPath(
      `${this.attribute.valuePath}.${valuePath}`,
      this.schemaId,
      rawValue,
      rawValue,
    );
  }

  getUnit(unit: string): string {
    const translateValue = `general.units.${unit}`;
    const translated = this.translate.instant(translateValue);
    if (translated === translateValue) {
      return unit;
    } else {
      return translated;
    }
  }
}
